/**
 * El slideshow del hero y las pestañas de colecciones.
 *
 * Client Component: son las dos interacciones de la home. El resto de la
 * página es server-rendered.
 *
 * Accesibilidad: los dots y las pestañas son `tablist`, así que se navegan
 * con las flechas. Las diapositivas fuera de la vista quedan con
 * `aria-hidden` para que el lector de pantalla no las lea.
 */
'use client';

import Link from 'next/link';
import { useCallback, useEffect, useRef, useState } from 'react';

const INTERVALO = 6000;

export interface Slide {
  titulo: string;
  subtitulo: string;
  href: string;
  cta: string;
  tema: 'claro' | 'oscuro';
  /**
   * Foto de campaña de la diapositiva.
   *
   * Opcional: si no viene, el slide se dibuja con el color de marca (el
   * comportamiento anterior). Cuando viene, la foto va **al lado** del
   * texto, no debajo: el texto va sobre un panel sólido de color de marca
   * para que el contraste lo den los tokens medidos de la marca y no un
   * velo negro que taparía la foto.
   */
  foto?: { src: string; alt: string; foco?: string } | null;
}

export function HeroSlides({ slides }: { slides: Slide[] }) {
  const [indice, setIndice] = useState(0);
  const pausado = useRef(false);

  const mostrar = useCallback(
    (nuevo: number) => {
      setIndice((nuevo + slides.length) % slides.length);
    },
    [slides.length],
  );

  // El auto-avance se frena si la persona está mirando o interactuando.
  useEffect(() => {
    if (slides.length < 2) return;
    const temporizador = setInterval(() => {
      if (!pausado.current) setIndice((n) => (n + 1) % slides.length);
    }, INTERVALO);
    return () => clearInterval(temporizador);
  }, [slides.length]);

  function onKeyDown(evento: React.KeyboardEvent) {
    if (evento.key === 'ArrowRight') {
      evento.preventDefault();
      mostrar(indice + 1);
    } else if (evento.key === 'ArrowLeft') {
      evento.preventDefault();
      mostrar(indice - 1);
    }
  }

  return (
    <section
      className="hero"
      aria-roledescription="carousel"
      aria-label="Novedades de la tienda"
      onKeyDown={onKeyDown}
      onMouseEnter={() => {
        pausado.current = true;
      }}
      onMouseLeave={() => {
        pausado.current = false;
      }}
      onFocus={() => {
        pausado.current = true;
      }}
      onBlur={() => {
        pausado.current = false;
      }}
    >
      <div
        className="hero__slides"
        data-hero-slides
        // El carrusel es un flex con desplazamiento: el transform mueve el
        // conjunto de slides (el CSS anima la transición).
        style={{ transform: `translateX(-${indice * 100}%)` }}
      >
        {slides.map((slide, i) => (
          <div
            key={slide.titulo}
            className={`hero__slide hero__slide--${slide.tema}${slide.foto ? ' hero__slide--con-foto' : ''}`}
            role="tabpanel"
            id={`slide-${i}`}
            aria-labelledby={`slide-dot-${i}`}
            data-slide={i}
            aria-hidden={i !== indice}
          >
            {slide.foto && (
              <div className="hero__media">
                {/* eslint-disable-next-line @next/next/no-img-element */}
                <img
                  src={slide.foto.src}
                  alt={slide.foto.alt}
                  width={1200}
                  height={1600}
                  /*
                   * `eager` en las tres, no `lazy` en la 2 y la 3: el
                   * carrusel se mueve con `translateX`, y el navegador NO
                   * reevalua el lazy-loading cuando lo que cambia es un
                   * transform. Las fotos quedaban sin cargar (se veia el
                   * panel de texto al lado de un hueco vacio) hasta que
                   * intervenia un scroll. Son 3 fotos de ~90 KB.
                   */
                  loading="eager"
                  decoding="async"
                  style={slide.foto.foco ? { objectPosition: slide.foto.foco } : undefined}
                />
              </div>
            )}
            <div className="hero__panel">
              {/*
                * El simbolo de la marca como sello: el manual lo define como
                * "simbolo de marca... uso como sello, badge o ilustracion de
                * cierre". Es decorativo, asi que va con `aria-hidden`.
                */}
              <span className="simbolo-marca simbolo-marca--estrellas" aria-hidden="true" />
              <p className="hero__title">{slide.titulo}</p>
              <p className="hero__subtitle">{slide.subtitulo}</p>
              <Link className="hero__cta" href={slide.href}>
                {slide.cta}
              </Link>
            </div>
          </div>
        ))}
      </div>
      <div className="hero__dots" role="tablist" aria-label="Elegir diapositiva">
        {slides.map((slide, i) => (
          <button
            key={slide.titulo}
            className={`hero__dot${i === indice ? ' is-active' : ''}`}
            type="button"
            role="tab"
            id={`slide-dot-${i}`}
            data-slide-to={i}
            aria-selected={i === indice}
            tabIndex={i === indice ? 0 : -1}
            aria-controls={`slide-${i}`}
            aria-label={`Ir a la diapositiva ${i + 1}: ${slide.titulo}`}
            onClick={() => mostrar(i)}
          />
        ))}
      </div>
    </section>
  );
}

export interface PanelColeccion {
  slug: string;
  nombre: string;
  contenido: React.ReactNode;
}

export function CollectionTabs({ paneles }: { paneles: PanelColeccion[] }) {
  const [activo, setActivo] = useState(paneles[0]?.slug ?? '');

  function onKeyDown(evento: React.KeyboardEvent) {
    const actual = paneles.findIndex((p) => p.slug === activo);
    if (actual < 0) return;
    let nuevo: number | null = null;
    if (evento.key === 'ArrowRight') nuevo = (actual + 1) % paneles.length;
    else if (evento.key === 'ArrowLeft') nuevo = (actual - 1 + paneles.length) % paneles.length;
    else if (evento.key === 'Home') nuevo = 0;
    else if (evento.key === 'End') nuevo = paneles.length - 1;
    if (nuevo === null) return;
    evento.preventDefault();
    setActivo(paneles[nuevo].slug);
  }

  return (
    <>
      <div className="option-group__values" role="tablist" aria-label="Colecciones por categoría" onKeyDown={onKeyDown}>
        {paneles.map((panel) => (
          <button
            key={panel.slug}
            className={`option-value${panel.slug === activo ? ' is-active' : ''}`}
            type="button"
            role="tab"
            id={`tab-${panel.slug}`}
            data-collection-tab={panel.slug}
            aria-selected={panel.slug === activo}
            tabIndex={panel.slug === activo ? 0 : -1}
            aria-controls={`panel-${panel.slug}`}
            onClick={() => setActivo(panel.slug)}
          >
            {panel.nombre}
          </button>
        ))}
      </div>
      {paneles.map((panel) => (
        <div
          key={panel.slug}
          className="collection-panel"
          role="tabpanel"
          id={`panel-${panel.slug}`}
          aria-labelledby={`tab-${panel.slug}`}
          data-collection-panel={panel.slug}
          hidden={panel.slug !== activo}
        >
          {panel.contenido}
        </div>
      ))}
    </>
  );
}
